import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import apiClient from "../apiClient";
import { useSnackbar } from 'notistack';

interface MyCampaignTableModalProps {
  showMyCampaignModal: boolean;
  setShowMyCampaignModal: Function;
  setUpdateCampaignFilter: Function;
};

interface Campaign {
  code: string;
  created_at: string;
  name: string;
  facilitator: string;
  description: string;
  access_level: string;
}

export default function MyCampaignTableModal({
  showMyCampaignModal,
  setShowMyCampaignModal,
  setUpdateCampaignFilter,
}: MyCampaignTableModalProps) {

  // notification
  const { enqueueSnackbar } = useSnackbar();

  // my campaign list
  const [campaignList, setCampaignList] = useState<Campaign[]>([]);

  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [editCampaign, setEditCampaign] = useState<Campaign | null>(null);

  const handleRowClick = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
  };

  const handleEditClick = (campaign: Campaign) => {
    setEditCampaign(campaign);
  };

  const handleCloseModal = () => {
    setSelectedCampaign(null);
    setEditCampaign(null);
  };

  const handleEditSave = async () => {
    if (!editCampaign) return;

    try {
      const response = await apiClient.patch(`/sigcap/api/update-campaign/${editCampaign.code}/`, {
        name: editCampaign.name,
        facilitator: editCampaign.facilitator,
        description: editCampaign.description,
        access_level: editCampaign.access_level,
      });

      enqueueSnackbar(response.data.message, {
        variant: 'success',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
      });

      // Update the campaign list locally
      setCampaignList((prev) =>
        prev.map((campaign) =>
          campaign.code === editCampaign.code ? { ...campaign, ...editCampaign } : campaign
        )
      );

      // Update the campaign filter
      setUpdateCampaignFilter((prev: boolean) => !prev);

      handleCloseModal();
    } catch (err) {
      enqueueSnackbar('Failed to update campaign', {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
      });
    }
  };

  // get my campaign list
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/sigcap/api/my-campaign-list/');
        setCampaignList(response.data.campaign_list);
      } catch (err) {
        enqueueSnackbar('Failed to fetch my campaign list', {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        });
      }
    };
    fetchData();
  }, [showMyCampaignModal, enqueueSnackbar]);

  // Helper function to format access level for display
  const formatAccessLevel = (level: string) => {
    switch (level) {
      case 'private':
        return 'Private';
      case 'authenticated':
        return 'Authenticated';
      case 'public':
        return 'Public';
      default:
        return level;
    }
  };

  return (
    <>
      <Modal open={showMyCampaignModal} onClose={() => setShowMyCampaignModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 900,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            My Campaigns
          </Typography>
          {/* Campaign Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Data Visibility</TableCell>
                  <TableCell>Edit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaignList.map((campaign) => (
                  <TableRow
                    key={campaign.code}
                    hover
                    onClick={() => handleRowClick(campaign)}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell>{campaign.name}</TableCell>
                    <TableCell>{campaign.code}</TableCell>
                    <TableCell>{campaign.created_at}</TableCell>
                    <TableCell>{formatAccessLevel(campaign.access_level)}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering row click
                          handleEditClick(campaign);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowMyCampaignModal(false)}
            sx={{
              mt: 2,
              display: "block",
              ml: "auto", // Aligns the button to the right
            }}
          >
            Close
          </Button>
        </Box>
      </Modal>
      {/* View Campaign Modal */}
      <Modal open={!!selectedCampaign} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              marginBottom: '1em',
            }}
          >
            Campaign Details
          </Typography>
          {selectedCampaign && (
            <>
              <Typography><strong>Name:</strong> {selectedCampaign.name}</Typography>
              <Typography><strong>Code:</strong> {selectedCampaign.code}</Typography>
              <Typography><strong>Created At:</strong> {selectedCampaign.created_at}</Typography>
              <Typography><strong>Facilitator:</strong> {selectedCampaign.facilitator}</Typography>
              <Typography><strong>Data Visibility:</strong> {formatAccessLevel(selectedCampaign.access_level)}</Typography>
              <Typography><strong>Description:</strong> {selectedCampaign.description}</Typography>
            </>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleCloseModal}
            sx={{
              mt: 2,
              display: "block",
              ml: "auto", // Aligns the button to the right
            }}
          >
            Close
          </Button>
        </Box>
      </Modal>

      {/* Edit Campaign Modal */}
      <Modal open={!!editCampaign} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              marginBottom: '1em',
            }}
          >
            Update Campaign Information
          </Typography>
          {editCampaign && (
            <>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  marginBottom: '1em',
                }}
              >
                Campaign ID:
                <span
                  style={{
                    fontWeight: 'bold',
                    marginLeft: '0.5em',
                    color: '#1976d2', // MUI primary color
                  }}
                >
                  {editCampaign.code}
                </span>
              </Typography>
              <TextField
                fullWidth
                label="Facilitator"
                value={editCampaign.facilitator}
                onChange={(e) =>
                  setEditCampaign({ ...editCampaign, facilitator: e.target.value })
                }
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Name (required*)"
                value={editCampaign.name}
                onChange={(e) =>
                  setEditCampaign({ ...editCampaign, name: e.target.value })
                }
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Data Visibility</InputLabel>
                <Select
                  value={editCampaign.access_level}
                  label="Data Visibility"
                  onChange={(e) =>
                    setEditCampaign({ ...editCampaign, access_level: e.target.value })
                  }
                >
                  <MenuItem value="private">Private - Only owner can access</MenuItem>
                  <MenuItem value="authenticated">Authenticated - Any logged in user can access</MenuItem>
                  <MenuItem value="public">Public - Anyone can access</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Description"
                value={editCampaign.description}
                onChange={(e) =>
                  setEditCampaign({ ...editCampaign, description: e.target.value })
                }
                multiline
                rows={4}
                sx={{ mb: 2 }}
              />
            </>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2, // Adds spacing between buttons
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEditSave}
              // disable when campaign name is empty
              disabled={!editCampaign?.name.trim()}
            >
              Update
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}